//Bibliotecas
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
//Componentes material
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
//Componentes
import Layout from './Layout';
import VitalizePaper from '../components/VitalizePaper';
import LabelSecao from '../components/LabelSecao';
import VitalizeFab from '../components/VitalizeFab';
//Controllers
import AvaliacaoController from '../controllers/AvaliacaoController';
import QuestionarioController from '../controllers/QuestionarioController';
import ClienteController from '../controllers/ClienteController';
import EmpresaController from '../controllers/EmpresaController';
import PermissaoController from '../controllers/PermissaoController';
//Utils
import deepCopy from '../utils/deepCopy';

/**
 * Página para inclusão de uma nova avalição
 */
class AvaliacaoAddPage extends Component {

    constructor(props) {
        super(props);
        //Bindings
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleEmpresaChange = this.handleEmpresaChange.bind(this);
        this.getEmpresaLabel = this.getEmpresaLabel.bind(this);
        this.getEmpresaSelected = this.getEmpresaSelected.bind(this);
        this.handleQuestionarioChange = this.handleQuestionarioChange.bind(this);
        this.getQuestionarioLabel = this.getQuestionarioLabel.bind(this);
        this.getQuestionarioSelected = this.getQuestionarioSelected.bind(this);
        this.handleClienteChange = this.handleClienteChange.bind(this);
        this.getClienteLabel = this.getClienteLabel.bind(this);
        this.getClienteSelected = this.getClienteSelected.bind(this);
        //Controllers
        this.avaliacaoController = new AvaliacaoController();
        this.questionarioController = new QuestionarioController();
        this.clienteController = new ClienteController();
        this.empresaController = new EmpresaController();
        this.permissaoController = new PermissaoController();
        //Estado inicial
        this.state = {
            erros: {
                empresa: '',
                cliente: '',
                questionario: ''
            },
            valores: {
                empresa: null,
                cliente: '',
                questionario: ''
            },
            questionarios: [],
            empresas: [],
            clientes: [],
            sucesso: false,
            processando: false,
            carregando: true,
            permiteSelecionarEmpresa: false,
            idAvaliacao: null,
        };
    }

    async componentDidMount() {
        //Cria uma cópia do estado e verifica se o usuário pode selecionar a empresa
        const state = deepCopy(this.state);
        state.carregando = false; //Indica que a consulta terminou
        state.permiteSelecionarEmpresa = await this.permissaoController.permiteSelecionarEmpresa();

        //Caso permita selecionar a empresa, carrega a listagem
        if (state.permiteSelecionarEmpresa) {
            state.empresas = await this.obterEmpresas();
        } else { //Caso contrário carrega os clientes e os questionários
            state.clientes = await this.obterClientes();
            state.questionarios = await this.obterQuestionarios();
        }

        //Atualiza o estado
        this.setState(state);
    }

    /**
     * Trata a alteração do campo empresa, recarregando os dados de clientes e questionários
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novaEmpresa Dados da empresa selecionada
     * @param {Number} novaEmpresa.id Identificação da empresa
     */
    async handleEmpresaChange(evt, novaEmpresa) {
        //Exibe loader
        this.setState({ carregando: true });
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        state.valores.empresa = novaEmpresa;
        state.carregando = false; //Indica que a consulta terminou

        //Se tiver uma empresa selecionada, pesquisa na API
        if (state.valores.empresa) {
            state.clientes = await this.obterClientes(state.valores.empresa.id);
            state.questionarios = await this.obterQuestionarios(state.valores.empresa.id);
        } else { //Se nenhuma tiver sido selecionada, deixa a lista vazia
            state.clientes = [];
            state.questionarios = [];
        }

        //Atualiza o estado da página
        this.setState(state);
    }
    
    /**
     * Retorna os dados da empresa para exibição no campo
     * @param {Object} empresa Dados da empresa
     * @param {String} empresa.nome Nome da empresa
     * @returns {String} Retorna o nome da empresa
     */
    getEmpresaLabel(empresa) {
        return empresa.nome;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getEmpresaSelected(opcao, valor) {
        //Compara a identificação das duas empresas
        return opcao.id === valor.id;
    }

    /**
     * Trata a alteração do campo questionário, recarregando os dados da avaliação
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novoQuestionario Dados do quesitonário selecionado
     * @param {Number} novoQuestionario.id Identificação do questionário
     */
    async handleQuestionarioChange(evt, novoQuestionario) {
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        state.valores.questionario = novoQuestionario;
        //Atualiza o status
        this.setState(state);
    }

    /**
     * Retorna os dados do questionário para exibição no campo
     * @param {Object} questionario Dados do questionário
     * @param {String} questionario.titulo Título do questionário
     * @returns {String} Retorna o título do questionário
     */
    getQuestionarioLabel(questionario) {
        return questionario.titulo;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getQuestionarioSelected(opcao, valor) {
        //Compara a identificação dos dois questionários
        return opcao.id === valor.id;
    }

    /**
     * Trata a alteração do campo cliente, recarregando os dados da avaliação
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novoCliente Dados do cliente selecionado
     * @param {Number} novoCliente.id Identificação do cliente
     */
    async handleClienteChange(evt, novoCliente) {
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        state.valores.cliente = novoCliente;
        //Atualiza o status
        this.setState(state);
    }
    
    /**
     * Retorna os dados do cliente para exibição no campo
     * @param {Object} cliente Dados do cliente
     * @param {String} cliente.titulo Título do cliente
     * @returns {String} Retorna o título do cliente
     */
    getClienteLabel(cliente) {
        return cliente.nome;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getClienteSelected(opcao, valor) {
        //Compara a identificação dos dois clientes
        return opcao.id === valor.id;
    }

    /**
     * Trata o evento de submissão do formulário, enviando os dados para a API
     * @param {*} event Dados do evento
     */
    async handleSubmit(event) {
        //Impede a propagação do evento
        event.preventDefault();
        //Exibe loader
        this.setState({ processando: true });
        let result;
        //Cria uma cópia do estado atual
        const state = deepCopy(this.state);
        state.processando = false; //Indica que o processamento terminou
        
        //Preenchendo objeto para repassar para o controller
        const avaliacao = {
            cliente: state.valores.cliente.id,
            questionario: state.valores.questionario.id,
            empresa: state.valores.empresa ? state.valores.empresa.id : null
        };

        //Envia os dados para a API
        result = await this.avaliacaoController.salvar(avaliacao);
        state.sucesso = result.sucesso;

        //Caso tenha sido salvo com sucesso, armazena o ID para redirecionamento
        if (result.sucesso) {
            state.idAvaliacao = result.avaliacao.id;
        } else { //Caso contrário, armazena a lista de erros
            state.erros = deepCopy(result.erros);
        }

        //Atualiza o estado
        this.setState(state);
    }

    /**
     * Carrega as empresas da API
     * @returns {Array} Retorna a lista de empresas, ou uma lista vazia em caso de erros
     */
    async obterEmpresas() {
        const result = await this.empresaController.listarSelecao();
        if (result.sucesso) 
            return result.items;
        else
            return [];
    }

    /**
     * Carrega os clientes da API
     * @param {Number} empresa Identificação da empresa selecionada
     * @returns {Array} Retorna a lista de clientes, ou uma lista vazia em caso de erros
     */
    async obterClientes(empresa) {
        const result = await this.clienteController.listarSelecao(empresa);
        if (result.sucesso) 
            return result.items;
        else
            return [];
    }

    /**
     * Carrega os questionário da API
     * @param {Number} empresa Identificação da empresa selecionada
     * @returns {Array} Retorna a lista de questionários, ou uma lista vazia em caso de erros
     */
    async obterQuestionarios(empresa) {
        const result = await this.questionarioController.listarSelecao(empresa);
        if (result.sucesso) 
            return result.items;
        else
            return [];
    }

    /**
     * Renderiza o campo de seleção de empresa, caso o usuário possua permissão 
     * @param {Boolean} permiteSelecionarEmpresa Indica se o usuário pode selecionar uma empresa
     * @param {String} empresa Valor selecionado
     * @param {String} empresaErro Mensagem de erro do campo
     * @param {Array} empresas Lista de empresas retornadas da API
     * @returns {React.Component} Retorna o componente ou null
     */
    renderCampoEmpresas(permiteSelecionarEmpresa, empresa, empresaErro, empresas) {
        if (permiteSelecionarEmpresa)
            return (
                <Grid item xs={12} >
                    <Autocomplete
                        id="select-empresa"
                        options={empresas}
                        value={empresa}
                        getOptionLabel={this.getEmpresaLabel}
                        getOptionSelected={this.getEmpresaSelected}
                        onChange={this.handleEmpresaChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="empresa"
                                name="empresa" 
                                label="Empresa"
                                native 
                                required
                                variant="outlined" 
                                error={!!empresaErro}
                                helperText={empresaErro}
                                fullWidth 
                            />
                        )}
                    />
                </Grid>
            );
        else
            return null;
    }

    render() {
        const { onLogout, usuario, empresa: empresaLogin, permissoes } = this.props;
        const { empresas, clientes, questionarios, permiteSelecionarEmpresa, sucesso, idAvaliacao, processando, carregando } = this.state;
        const { empresa: empresaErro, cliente: clienteErro, questionario: questionarioErro } = this.state.erros;
        const { empresa, cliente, questionario } = this.state.valores;


        if (sucesso) return (<Redirect to={`/avaliacao/${idAvaliacao}`} />);

        return (
            <Layout onLogout={onLogout} returnLink="/avaliacao" usuario={usuario} empresa={empresaLogin} permissoes={permissoes} carregando={carregando} >
                <CssBaseline />
                <form onSubmit={this.handleSubmit} noValidate >
                    <VitalizePaper>
                        <Grid container spacing={1}>
                            <Grid item xs={12} >
                                <LabelSecao label="Iniciar nova avaliação" />
                            </Grid>
                            {
                                this.renderCampoEmpresas(permiteSelecionarEmpresa, empresa, empresaErro, empresas)
                            }
                            <Grid item xs={12} sm={6} >
                                <Autocomplete
                                    id="select-cliente"
                                    options={clientes}
                                    value={cliente}
                                    getOptionLabel={this.getClienteLabel}
                                    getOptionSelected={this.getClienteSelected}
                                    onChange={this.handleClienteChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            id="cliente"
                                            name="cliente" 
                                            label="Cliente"
                                            native 
                                            required
                                            variant="outlined" 
                                            error={!!clienteErro}
                                            helperText={clienteErro}
                                            fullWidth 
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <Autocomplete
                                    id="select-questionario"
                                    options={questionarios}
                                    value={questionario}
                                    getOptionLabel={this.getQuestionarioLabel}
                                    getOptionSelected={this.getQuestionarioSelected}
                                    onChange={this.handleQuestionarioChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            id="questionario"
                                            name="questionario" 
                                            label="Questionário"
                                            native 
                                            required
                                            variant="outlined" 
                                            error={!!questionarioErro}
                                            helperText={questionarioErro}
                                            fullWidth 
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </VitalizePaper>
                    <VitalizeFab color="primary" variant="extended" type="submit" loading={processando} >
                            Iniciar
                    </VitalizeFab>
                </form>
            </Layout>
        );
    }

}

AvaliacaoAddPage.propTypes = {
    /** Evento disparado quando o usuário realiza o logout */
    onLogout: PropTypes.func,
    /** Dados do usuário autenticado */
    usuario: PropTypes.object.isRequired,
    /** Empresa relacionada ao usuário */
    empresa: PropTypes.object,
    /** Objeto contendo as permissões do usuário */
    permissoes: PropTypes.object.isRequired
};

export default AvaliacaoAddPage;